/**
 * ChipSelect — a set of values held *inside* one control: a chip per value, each removable,
 * and a picker for the next one.
 *
 * **It is a native `<select>` in a box, not a hand-written combobox.** The obvious reading of
 * "chips in the select" is a custom listbox — a popup, roving focus, `aria-activedescendant`,
 * typeahead, and a scroll container that has to behave on a phone. Every one of those is a
 * thing to get wrong, and an agent-authored one gets them wrong quietly, in the half of the
 * product nobody tests with a keyboard. So the picker here stays the platform's: it opens,
 * types ahead, scrolls and reads out correctly because it is the real control, and this file
 * only lends it a border it shares with the chips. If a search field inside the popup is ever
 * genuinely needed, that is the moment to write the listbox — and it will be one component,
 * here, not a second dialect per screen.
 *
 * **It knows nothing about what it is holding.** Every word a person reads arrives as a prop
 * (`addLabel`, `removeLabel`), the way `Slicer` takes its facet copy: this control has no copy
 * of its own to translate, which is also why it adds no keys to the `app` namespace.
 *
 * **The picker is an adder, not a state.** Its value is always `''` — each pick is one act,
 * reported through `onAdd`, and what is *held* is the chips. The caller owns the set and is
 * free to interpose a confirmation between the pick and the change (the task screen states an
 * admission before it performs one, `KLQ-20260821-001` §F), which a control that mutated its
 * own value would have already made true.
 */

import { ChevronDown, X } from 'lucide-react';

import styles from './ChipSelect.module.css';

/** The same two the shared `Select` has, and the same two heights. */
export type ChipSelectSize = 'md' | 'sm';

export interface ChipSelectOption {
  readonly id: string;
  readonly label: string;
}

export interface ChipSelectProps {
  /** What is held, in order, one chip each. */
  readonly tokens: readonly ChipSelectOption[];
  /** What may still be picked. The caller filters out what is already held. */
  readonly options: readonly ChipSelectOption[];
  /** The picker's resting text, and its accessible name — *Add a person…*. */
  readonly addLabel: string;
  /** The accessible name of one chip's cross, given that chip's label. */
  readonly removeLabel: (label: string) => string;
  readonly onAdd: (id: string) => void;
  readonly onRemove: (id: string) => void;
  /** `md` is the form field, `sm` the toolbar's — `Select`'s sizes, verbatim (§C). */
  readonly size?: ChipSelectSize;
  /** The whole control: neither adding nor removing is available. */
  readonly disabled?: boolean;
  /** The picker alone. Removal survives it — a caller that cannot yet *state* what an
      addition would do (the task screen waits for the audience) can still take somebody
      off, and a set that could only grow while a query was in flight would be worse. */
  readonly addDisabled?: boolean;
  /** One line, never two: the chips shrink to an ellipsis instead of wrapping. For a control
      inside a fixed-height row — the task register's assignee column, whose window
      (`useAnchoredWindow`) needs every row the same height, so a box that grew a second line
      of chips would be cut off by the row rather than drawn taller than it. The full name
      stays in the chip's `title` and in its cross's accessible name. */
  readonly singleLine?: boolean;
}

export function ChipSelect({
  tokens,
  options,
  addLabel,
  removeLabel,
  onAdd,
  onRemove,
  size = 'md',
  disabled = false,
  addDisabled = false,
  singleLine = false,
}: ChipSelectProps) {
  // With nothing left to pick the picker holds only its own placeholder, which is not an
  // offer. It stays rendered and disabled rather than disappearing: a control that vanishes
  // when the last option is taken makes the field jump every time somebody edits the set.
  const pickerDisabled = disabled || addDisabled || options.length === 0;

  return (
    <div
      className={[
        styles.field,
        styles[size],
        singleLine ? styles.singleLine : '',
        disabled ? styles.disabled : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {tokens.map((token) => (
        <span
          key={token.id}
          className={styles.token}
          {...(singleLine ? { title: token.label } : {})}
        >
          <span className={styles.tokenLabel}>{token.label}</span>
          <button
            type="button"
            className={styles.remove}
            disabled={disabled}
            aria-label={removeLabel(token.label)}
            onClick={() => {
              onRemove(token.id);
            }}
          >
            <X size={12} strokeWidth={1.5} aria-hidden />
          </button>
        </span>
      ))}

      {/* **Not `components/Select`, and that is the one exception.** This is not a field —
          it is the adder *inside* the chip box, borderless, so that an empty field and a full
          one are the same height. Giving it the shared control's border would draw a box
          inside a box. `Select`'s own note names `ChipSelect` as one of the two richer
          controls it defers to; this is the other side of that sentence.

          What it does take from `Select` as of `KLQ-20260910-001` §C is the *reset* and the
          chevron that goes with it — the box is the exception, the platform's own dropdown
          button never was. */}
      <select
        className={styles.picker}
        value=""
        disabled={pickerDisabled}
        aria-label={addLabel}
        onChange={(event) => {
          const picked = event.target.value;
          if (picked === '') return;
          onAdd(picked);
        }}
      >
        <option value="">{addLabel}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} strokeWidth={1.5} className={styles.chevron} aria-hidden />
    </div>
  );
}
